import { ReactGoogleAutocompleteInputProps } from 'react-google-autocomplete';
import {
  useRef,
  useState,
  useEffect,
  useCallback,
  InputHTMLAttributes,
} from 'react';

import { Config } from 'config';
import {
  InputContainer,
  InputContainerProps,
} from 'components/input-container';

import * as Styles from './styles';
import { awaitPacContainer } from './pac-container';
import {
  loadPlaces,
  getGoogleMaps,
  PlacesListener,
  placesAuthFailed,
  onPlacesAuthFailure,
  reportPlacesFailure,
  PlacesFailureReason,
} from './load-places';

const defaultOptions = {
  types: ['geocode'],
  componentRestrictions: { country: 'US' },
  fields: ['address_components', 'formatted_address', 'place_id'],
};

/** How long Google gets to answer the first typed address. */
export const SUGGESTION_TIMEOUT_MS = 4000;
const MIN_TYPED_CHARS = 3;
// A real answer: either suggestions or "nothing matches". Anything else is
// an error (REQUEST_DENIED, OVER_QUERY_LIMIT, UNKNOWN_ERROR, ...).
const ANSWERED = new Set(['OK', 'ZERO_RESULTS']);

/**
 * Silent automatic retries (owner, 3 Nov): no button, no manual mode. After
 * a failure the field re-attaches a fresh Autocomplete on the next keystroke
 * or focus once the delay has passed (or at once if it is focused then):
 * 2 s, 5 s, 15 s, then every 30 s.
 */
export const RETRY_DELAYS_MS = [2000, 5000, 15000, 30000];
/** The first failure plus one failed automatic retry show the hint. */
export const HINT_AFTER_FAILURES = 2;
export const SUGGESTIONS_HINT =
  "Address suggestions aren't loading — you can type the full address.";

/**
 * After a key refusal Google disables the field and writes "Oops! Something
 * went wrong." into it. Manual typing must keep working, so undo that.
 */
const keepInputUsable = (
  node: HTMLInputElement,
  disabled: boolean,
  placeholder?: string
) => {
  if (node.disabled !== disabled) {
    node.disabled = disabled;
  }
  if (node.classList.contains('gm-err-autocomplete')) {
    node.classList.remove('gm-err-autocomplete');
  }
  if (node.style.backgroundImage) {
    node.style.backgroundImage = '';
  }
  const current = node.getAttribute('placeholder') ?? undefined;
  if (current !== placeholder) {
    if (placeholder === undefined) {
      node.removeAttribute('placeholder');
    } else {
      node.setAttribute('placeholder', placeholder);
    }
  }
};

export type AddressAutocompleteInputProps = InputContainerProps &
  ReactGoogleAutocompleteInputProps &
  InputHTMLAttributes<HTMLInputElement>;

export const AddressAutoCompleteInput = ({
  label,
  value,
  style,
  error,
  large,
  footer,
  className,
  onPlaceSelected,
  language = 'en',
  libraries = ['places'],
  googleMapsScriptBaseUrl,
  options = defaultOptions,
  apiKey = Config.googleMapsApiKey,
  inputAutocompleteValue = 'new-password',
  ...props
}: AddressAutocompleteInputProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [inputNode, setInputNode] = useState<HTMLInputElement | null>(null);
  // A callback ref: a new input node (remount, key change) re-attaches.
  const setInput = useCallback((node: HTMLInputElement | null) => {
    inputRef.current = node;
    setInputNode(node);
  }, []);
  const callbackRef = useRef(onPlaceSelected);
  callbackRef.current = onPlaceSelected;
  const inputStateRef = useRef({
    placeholder: props.placeholder,
    disabled: Boolean(props.disabled),
  });
  inputStateRef.current = {
    placeholder: props.placeholder,
    disabled: Boolean(props.disabled),
  };
  const [hint, setHint] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // Failed attempts in a row; survives re-attaching.
  const failuresRef = useRef(0);
  const retryRef = useRef<{ due: boolean; timer?: number }>({ due: false });

  const clearRetry = useCallback(() => {
    window.clearTimeout(retryRef.current.timer);
    retryRef.current = { due: false };
  }, []);
  // Google answered: back to normal, hint gone.
  const working = useCallback(() => {
    failuresRef.current = 0;
    clearRetry();
    setHint(false);
  }, [clearRetry]);
  const retryNow = useCallback(() => {
    clearRetry();
    setAttempt((value) => value + 1);
  }, [clearRetry]);
  const scheduleRetry = useCallback(() => {
    failuresRef.current += 1;
    const failures = failuresRef.current;
    if (failures >= HINT_AFTER_FAILURES) {
      setHint(true);
    }
    const delay =
      RETRY_DELAYS_MS[Math.min(failures, RETRY_DELAYS_MS.length) - 1];
    clearRetry();
    retryRef.current.timer = window.setTimeout(() => {
      retryRef.current.timer = undefined;
      if (inputRef.current && document.activeElement === inputRef.current) {
        retryNow();
      } else {
        retryRef.current.due = true;
      }
    }, delay);
  }, [clearRetry, retryNow]);
  useEffect(() => clearRetry, [clearRetry]);
  const optionsKey = JSON.stringify(options);
  const librariesKey = libraries.join(',');

  useEffect(() => {
    const node = inputNode;
    if (!node) {
      return undefined;
    }
    let active = true;
    let listener: PlacesListener | undefined;
    let autocomplete: object | undefined;
    // This field's own dropdown, found when Google creates it (later).
    let ownContainer: HTMLElement | undefined;
    let stopWaiting: (() => void) | undefined;
    let timer: number | undefined;
    let settled = false;
    let checking = false;
    let guard: MutationObserver | undefined;
    let timedOut = false;
    let watcher: MutationObserver | undefined;
    // Only this field's own dropdown counts, never another field's.
    const suggestionsShown = () =>
      Boolean(
        ownContainer &&
        ownContainer.style.display !== 'none' &&
        ownContainer.querySelector('.pac-item')
      );
    // Google answered (also late, after a timeout): clear the hint and any
    // pending retry.
    const answered = () => {
      if (!active) {
        return;
      }
      const wasFailing = timedOut || failuresRef.current > 0;
      timedOut = false;
      settled = true;
      window.clearTimeout(timer);
      if (wasFailing) {
        working();
      }
    };
    // Suggestions appearing in this field's own dropdown are an answer.
    const watchOwnDropdown = () => {
      if (watcher || !ownContainer || typeof MutationObserver === 'undefined') {
        return;
      }
      watcher = new MutationObserver(() => {
        if (suggestionsShown()) {
          answered();
        }
      });
      watcher.observe(ownContainer, {
        subtree: true,
        childList: true,
        attributes: true,
      });
    };
    const merged = { ...defaultOptions, ...JSON.parse(optionsKey) };

    const fail = (reason: PlacesFailureReason, status?: string) => {
      if (!active) {
        return;
      }
      if (reason === 'auth' && !guard) {
        const restore = () =>
          keepInputUsable(
            node,
            inputStateRef.current.disabled,
            inputStateRef.current.placeholder
          );
        restore();
        if (typeof MutationObserver !== 'undefined') {
          guard = new MutationObserver(restore);
          guard.observe(node, {
            attributes: true,
            attributeFilter: ['disabled', 'placeholder', 'class', 'style'],
          });
        }
      }
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timer);
      reportPlacesFailure(reason, status);
      if (reason === 'timeout') {
        timedOut = true;
      }
      scheduleRetry();
    };

    // Detection: the first time the user types 3+ characters (or pastes),
    // ask Google's AutocompleteService for the same text. Its status tells a
    // real "no match" (ZERO_RESULTS) from an error, and a missing answer
    // after SUGGESTION_TIMEOUT_MS means Google is not answering. Suggestions
    // already on screen also count as an answer. One probe per attachment.
    const check = (event?: Event) => {
      if (!active) {
        return;
      }
      if (retryRef.current.due) {
        retryNow();
        return;
      }
      if (settled || checking) {
        return;
      }
      const text = node.value.trim();
      const pasted =
        (event as InputEvent | undefined)?.inputType === 'insertFromPaste';
      if (text.length < MIN_TYPED_CHARS && !(pasted && text)) {
        return;
      }
      checking = true;
      const Service = getGoogleMaps()?.places?.AutocompleteService;
      if (Service) {
        try {
          const result = new Service().getPlacePredictions(
            {
              input: text,
              types: merged.types,
              componentRestrictions: merged.componentRestrictions,
            },
            (_predictions, status) => {
              if (!active) {
                return;
              }
              if (ANSWERED.has(status) || suggestionsShown()) {
                answered();
              } else {
                fail('error', status);
              }
            }
          );
          // Newer Maps also returns a promise that rejects on errors; the
          // callback above already handles them.
          (result as Promise<unknown> | undefined)?.catch?.(() => undefined);
        } catch {
          fail('error');
          return;
        }
      }
      timer = window.setTimeout(() => {
        if (settled || !active) {
          return;
        }
        if (suggestionsShown()) {
          answered();
        } else {
          fail('timeout');
        }
      }, SUGGESTION_TIMEOUT_MS);
    };

    const onFocus = () => {
      if (active && retryRef.current.due) {
        retryNow();
      }
    };
    node.addEventListener('focus', onFocus);
    const stopAuth = onPlacesAuthFailure(() => fail('auth'));
    loadPlaces(
      apiKey || '',
      language,
      librariesKey.split(','),
      googleMapsScriptBaseUrl
    )
      .then(() => {
        if (!active) {
          return;
        }
        if (placesAuthFailed()) {
          fail('auth');
          return;
        }
        const Autocomplete = getGoogleMaps()?.places?.Autocomplete;
        if (!Autocomplete) {
          throw new Error('Address suggestions unavailable');
        }
        // Start waiting before Google can create the dropdown.
        stopWaiting = awaitPacContainer(node, (container) => {
          if (!active) {
            container.remove();
            return;
          }
          ownContainer = container;
          if (suggestionsShown()) {
            answered();
          }
          watchOwnDropdown();
        });
        const created = new Autocomplete(node, merged);
        autocomplete = created;
        listener = created.addListener('place_changed', () => {
          callbackRef.current?.(
            created.getPlace(),
            inputRef as React.RefObject<HTMLInputElement>,
            { current: created }
          );
        });
        node.addEventListener('input', check);
        // A silent retry re-probes at once when there is text to check.
        if (failuresRef.current > 0 && document.activeElement === node) {
          check();
        }
      })
      .catch(() => fail('load'));

    return () => {
      active = false;
      window.clearTimeout(timer);
      stopAuth();
      guard?.disconnect();
      watcher?.disconnect();
      node.removeEventListener('input', check);
      node.removeEventListener('focus', onFocus);
      listener?.remove();
      if (autocomplete) {
        const clear = getGoogleMaps()?.event?.clearInstanceListeners;
        clear?.(autocomplete);
        clear?.(node);
      }
      stopWaiting?.();
      // Only a dropdown claimed while this field had focus, or as the only
      // waiting field (pac-container.ts), so never another field's.
      ownContainer?.remove();
    };
  }, [
    inputNode,
    apiKey,
    language,
    librariesKey,
    googleMapsScriptBaseUrl,
    optionsKey,
    attempt,
    working,
    retryNow,
    scheduleRetry,
  ]);

  return (
    <InputContainer
      label={label}
      style={style}
      value={value}
      error={error}
      large={large}
      footer={footer}
      className={className}
      below={
        hint ? (
          <Styles.Hint role="status">{SUGGESTIONS_HINT}</Styles.Hint>
        ) : undefined
      }
    >
      <Styles.Input
        id={label}
        error={error}
        value={value}
        ref={setInput}
        autoComplete={inputAutocompleteValue}
        {...props}
      />
    </InputContainer>
  );
};
